<script setup lang="ts">
import { computed } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import LineChartDownIcon from "@/assets/icons/line-chart-down.svg?component";
import LineChartUpIcon from "@/assets/icons/line-chart-up.svg?component";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { MonositeConsumption, SectionConsumption } from "@/domains/v4b/site-consumption/monosite-consumption.types";
import { useTypedIntl } from "@/locales/typed-intl";

import TopFlopCard, { ITopFlopItem } from "@/components/data/dashboard/top-flop-card.vue";

interface IProps {
  sectionsConsumption?: MonositeConsumption["devicesBySection"];
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

function filterByConsumption(sectionDevicesConsumption: SectionConsumption) {
  return !!sectionDevicesConsumption.total.value;
}

function filterByEvolutionGreaterThanZero(sectionDevicesConsumption: SectionConsumption) {
  return !!sectionDevicesConsumption.evolution.value && sectionDevicesConsumption.evolution.value > 0;
}

function filterByEvolutionLessThanZero(sectionDevicesConsumption: SectionConsumption) {
  return !!sectionDevicesConsumption.evolution.value && sectionDevicesConsumption.evolution.value < 0;
}

function mapConsumption(sectionDevicesConsumption: SectionConsumption): ITopFlopItem {
  return {
    value: sectionDevicesConsumption.total.value as number,
    unit: sectionDevicesConsumption.total.unit,
    siteName: sectionDevicesConsumption.sectionName,
  };
}

function mapEvolution(sectionDevicesConsumption: SectionConsumption): ITopFlopItem {
  return {
    value: sectionDevicesConsumption.evolution.value as number,
    unit: sectionDevicesConsumption.evolution.unit,
    siteName: sectionDevicesConsumption.sectionName,
  };
}

function generatePodium(
  filter: (sectionDevicesConsumption: SectionConsumption) => boolean,
  map: (sectionDevicesConsumption: SectionConsumption) => ITopFlopItem,
  sort: "asc" | "desc" = "desc",
) {
  if (!props.sectionsConsumption) return [];

  return props.sectionsConsumption
    .filter(filter)
    .map(map)
    .sort((a, b) => (sort === "desc" ? b.value - a.value : a.value - b.value))
    .slice(0, 3);
}

const top3HighestConsumptionSections = computed<ITopFlopItem[]>(() => {
  return generatePodium(filterByConsumption, mapConsumption);
});

const top3HighestConsumptionIncreaseSections = computed(() => {
  return generatePodium(filterByEvolutionGreaterThanZero, mapEvolution);
});

const top3HighestConsumptionDropSections = computed(() => {
  return generatePodium(filterByEvolutionLessThanZero, mapEvolution, "asc");
});
</script>

<template>
  <div class="max-xl:flex flex-col xl:grid grid-flow-col grid-cols-3 gap-6">
    <TopFlopCard
      :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.mostEnergyConsummingPodium"
      :is-loading="props.isLoading"
      :items="top3HighestConsumptionSections"
      :title="t(k.data.dashboard.topFlops.highestConsumption[EDashboardType.MONOSITE])"
    >
      <template #title-icon>
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <i class="pi pi-bolt !text-xs text-text-secondary" />
        </div>
      </template>
    </TopFlopCard>

    <TopFlopCard
      :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.highestConsumptionIncreasePodium"
      :is-loading="props.isLoading"
      :items="top3HighestConsumptionIncreaseSections"
      :title="t(k.data.dashboard.topFlops.highestPositiveEvolution[EDashboardType.MONOSITE])"
    >
      <template #title-icon>
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <LineChartUpIcon />
        </div>
      </template>
    </TopFlopCard>

    <TopFlopCard
      :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.highestConsumptionDecreasePodium"
      :is-loading="props.isLoading"
      :items="top3HighestConsumptionDropSections"
      :title="t(k.data.dashboard.topFlops.highestNegativeEvolution[EDashboardType.MONOSITE])"
    >
      <template #title-icon>
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <LineChartDownIcon />
        </div>
      </template>
    </TopFlopCard>
  </div>
</template>
